import * as React from "react";

import { datadogRum } from "@datadog/browser-rum";

// This is a tracking-only API key scoped to org.tracking — it can only initialize the chat widget.
const KUSTOMER_API_KEY =
  "eyJhbGciOiJub25lIn0.eyJvcmciOiI2OTY1MjNlZGNjYjNlMGU5NTMyYWVhODMiLCJvcmdOYW1lIjoidmVyc2VtZWRpY2FsIiwicG9kIjoicHJvZDEiLCJyb2xlcyI6WyJvcmcudHJhY2tpbmciXX0.";
const KUSTOMER_BRAND_ID = "696523f7cc3fd3adc0d301f0";

export interface KustomerContext {
  email: string;
  phone: string;
  departmentId: number | null;
  orderNumber: string | null;
}

const useKustomerWidget = (enabled: boolean, context: KustomerContext) => {
  const [kustomerReady, setKustomerReady] = React.useState(false);
  const listenerAddedRef = React.useRef(false);
  const contextRef = React.useRef(context);
  contextRef.current = context;

  React.useEffect(() => {
    // Skip Kustomer widget in Playwright tests (flag set by base-test fixture)
    if (!enabled || (window as any).Playwright) return;

    const onKustomerLoaded = () => {
      window.Kustomer?.start({ brandId: KUSTOMER_BRAND_ID }, () => {
        setKustomerReady(true);
      });
    };
    window.addEventListener("kustomerLoaded", onKustomerLoaded);

    const script = document.createElement("script");
    script.src = "https://cdn.kustomerapp.com/chat-web/widget.js";
    script.setAttribute("data-kustomer-api-key", KUSTOMER_API_KEY);
    script.async = true;
    document.body.appendChild(script);

    return () => {
      window.removeEventListener("kustomerLoaded", onKustomerLoaded);
      setKustomerReady(false);
      listenerAddedRef.current = false;
      if (window.Kustomer) {
        window.Kustomer.stop();
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [enabled]);

  React.useEffect(() => {
    if (!kustomerReady) return;

    window.Kustomer?.describeCustomer(
      {
        attributes: {
          sharedEmails: context.email ? [context.email] : [],
          sharedPhones: context.phone ? [context.phone] : [],
        },
        customAttributes: {
          contactDeptIdStr: context.departmentId?.toString() ?? null,
        },
      },
      (_res, error) => {
        if (error) {
          console.error("Kustomer describeCustomer failed:", error);
        }
      },
    );
  }, [kustomerReady, context.email, context.phone, context.departmentId]);

  React.useEffect(() => {
    if (!kustomerReady || listenerAddedRef.current) return;
    listenerAddedRef.current = true;

    window.Kustomer?.addListener("onConversationCreate", (res, error) => {
      if (error || !res?.conversationId) return;

      const datadogSessionStr =
        datadogRum.getInternalContext()?.session_id ?? null;
      const customAttributes = {
        ...(contextRef.current.orderNumber
          ? { orderNumberStr: contextRef.current.orderNumber }
          : {}),
        datadogSessionStr,
      };

      window.Kustomer?.describeConversation(
        { conversationId: res.conversationId, customAttributes },
        (_res, err) => {
          if (err) {
            console.error("Kustomer describeConversation failed:", err);
          }
        },
      );
    });
  }, [kustomerReady]);

  return kustomerReady;
};

export default useKustomerWidget;
